import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { CONTACT } from "@/data/content";

const WAVE_IMG =
  "https://images.unsplash.com/photo-1558591710-4b4a1ae0f04d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwyfHxtaW5pbWFsaXN0JTIwYWJzdHJhY3QlMjB3aGl0ZSUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzg1NDA3MTQ0fDA&ixlib=rb-4.1.0&q=85";

export const Guarantee = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.1, 1.25]);

  return (
    <section
      ref={ref}
      data-testid="guarantee-section"
      className="relative overflow-hidden bg-forest text-white px-6 md:px-12 py-28 md:py-40"
    >
      <motion.div
        aria-hidden
        style={{ y, scale, backgroundImage: `url(${WAVE_IMG})` }}
        className="pointer-events-none absolute inset-0 bg-cover bg-center opacity-[0.08] mix-blend-screen"
      />
      <div className="pointer-events-none absolute inset-0 grain opacity-[0.04]" aria-hidden />

      <div className="relative mx-auto max-w-[1200px] text-center">
        <motion.span
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="inline-flex items-center gap-2 rounded-full border border-white/20 px-4 py-1.5 font-mono text-xs uppercase tracking-[0.2em] text-lime"
        >
          <span className="h-1.5 w-1.5 rounded-full bg-lime" />
          Performance guarantee
        </motion.span>

        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
          className="mt-8 font-display font-extrabold tracking-tighter leading-[1.02] text-3xl sm:text-5xl lg:text-6xl max-w-[20ch] mx-auto text-balance"
        >
          No ranking improvement in 3 months?
          <span className="text-lime"> You won&apos;t be billed again</span> until you
          start seeing results.
        </motion.h2>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="mt-8 text-white/70 text-lg max-w-xl mx-auto leading-relaxed"
        >
          We stand by our work. If you&apos;re not ranking, you&apos;re not paying — it&apos;s that simple.
        </motion.p>

        <motion.a
          href={CONTACT.calendly}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="guarantee-cta"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="group mt-10 inline-flex items-center gap-3 rounded-full bg-lime px-8 py-4 text-base font-semibold text-forestdeep hover:bg-white transition-colors duration-300"
        >
          Start risk-free
          <span className="inline-block transition-transform duration-300 group-hover:translate-x-1.5">→</span>
        </motion.a>
      </div>
    </section>
  );
};
